import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCauzioniCatalogue,
  suggestCauzioniFromDocument,
} from '../../services/axios-client/axeCauzioni';
import CauzioniInfoModal from './CauzioniInfoModal';
import { clearDocumentPrefill, storeDocumentPrefill } from './documentPrefill';

const MAX_DOCUMENT_MB = 8;

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.replace(`data:${file.type};base64,`, ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function CauzioniSubProducts() {
  const navigate = useNavigate();
  const [subProducts, setSubProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [infoSubProduct, setInfoSubProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // AI document analysis. `analysis` holds the whole reply so the reason for each match can be
  // shown on its card, not just the ids used to narrow the grid.
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    getCauzioniCatalogue()
      .then((res) => setSubProducts(res.sub_products))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  /* Ordered by the AI's own ranking rather than by catalogue order, because the ranking is the
     answer — the first card is the one it is most confident about. */
  const suggestedIds = useMemo(
    () => (analysis?.suggestions ?? []).map((s) => s.sub_product_id),
    [analysis],
  );

  const reasonBySubProduct = useMemo(() => new Map(
    (analysis?.suggestions ?? []).map((s) => [s.sub_product_id, s]),
  ), [analysis]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    const bySearch = term
      ? subProducts.filter((sub) => sub.name.toLowerCase().includes(term)
        || sub.risks.some((risk) => risk.name.toLowerCase().includes(term)))
      : subProducts;

    if (!suggestedIds.length) return bySearch;

    return bySearch
      .filter((sub) => suggestedIds.includes(sub.id))
      .sort((a, b) => suggestedIds.indexOf(a.id) - suggestedIds.indexOf(b.id));
  }, [subProducts, search, suggestedIds]);

  const handleDocument = async (event) => {
    const file = event.target.files?.[0];
    // Cleared immediately so re-picking the same file after an error still fires onChange.
    event.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setAnalysisError('Carica un file PDF.');
      return;
    }

    if (file.size > MAX_DOCUMENT_MB * 1024 * 1024) {
      setAnalysisError(`Il documento supera i ${MAX_DOCUMENT_MB} MB.`);
      return;
    }

    setAnalysing(true);
    setAnalysisError('');
    setAnalysis(null);
    // Drop the previous document's readings before the new ones land, so a failed second upload
    // cannot leave the form quietly prefilled from a document the broker has moved on from.
    clearDocumentPrefill();

    try {
      const response = await suggestCauzioniFromDocument(await readAsBase64(file), file.name);

      if (response.status !== 200) {
        setAnalysisError(response.data?.error || 'Analisi non riuscita. Riprova.');
        return;
      }

      if (response.data.unreadable) {
        setAnalysisError('Non siamo riusciti a leggere il PDF: se è una scansione, prova con il file originale.');
        return;
      }

      if (!response.data.requires_guarantee || !response.data.suggestions?.length) {
        setAnalysisError('Non abbiamo riconosciuto una garanzia richiesta da questo documento. Scegli manualmente qui sotto.');
        return;
      }

      setAnalysis(response.data);
      /* Kept even if the broker then clears the filter and picks a different garanzia by hand:
         the contraente and beneficiario the document names are still the client's. */
      storeDocumentPrefill(response.data.extracted);
    } catch (e) {
      setAnalysisError('Analisi non riuscita. Riprova.');
    } finally {
      setAnalysing(false);
    }
  };

  const clearAnalysis = () => {
    setAnalysis(null);
    setAnalysisError('');
  };

  /* Every garanzia goes through the rischio screen, including the 25 that only have one.
     Skipping it for those made the journey inconsistent — the broker sometimes chose a rischio
     and sometimes landed straight on the form — and it hid which rischio was actually being
     requested. A single rischio is simply presented on its own for selection. */
  const onRequestPolicy = (sub) => navigate(`/quote/surety-bonds/${sub.slug}/risks`);

  return (
    <div className="cauzioni-page" data-testid="cauzioni-sub-products">
      <div className="cauzioni-head">
        <h1>FIDEIUSSIONI E CAUZIONI</h1>
        <p>Scegli la garanzia da richiedere</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="d-none"
        onChange={handleDocument}
        data-testid="cauzioni-ai-input"
      />

      {!analysis && (
        <div className="cauzioni-ai-panel">
          <div className="cauzioni-ai-copy">
            <h2>Non sai quale garanzia serve al cliente?</h2>
            <p>
              Carica il bando, il disciplinare o la richiesta di anticipazione: leggiamo il
              documento e ti mostriamo le garanzie adatte.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-questionnaire"
            onClick={() => fileInputRef.current?.click()}
            disabled={analysing}
            data-testid="cauzioni-ai-upload"
          >
            {analysing ? 'Analisi in corso...' : 'Carica il documento'}
          </button>
        </div>
      )}

      {analysisError && (
        <p className="cauzioni-count cauzioni-error" data-testid="cauzioni-ai-error">
          {analysisError}
        </p>
      )}

      {analysis && (
        <div className="cauzioni-ai-result-compact" data-testid="cauzioni-ai-result">
          <div className="cauzioni-ai-result-content">
            <span className="cauzioni-badge">Analisi del documento</span>
            <p className="cauzioni-ai-summary">{analysis.document_summary}</p>
            {analysis.extracted?.garanzia?.importo_garantito != null && (
              <p className="cauzioni-ai-extracted">
                {`Importo da garantire rilevato: ${Number(analysis.extracted.garanzia.importo_garantito)
                  .toLocaleString('it-IT', { minimumFractionDigits: 2 })} €`}
                {analysis.extracted.beneficiario?.denominazione
                  ? ` · Beneficiario: ${analysis.extracted.beneficiario.denominazione}`
                  : ''}
              </p>
            )}
            <p className="cauzioni-ai-extracted mb-0">
              I dati del documento verranno precompilati nel modulo di richiesta.
            </p>
          </div>
          <button type="button" className="cauzioni-ai-dismiss" onClick={clearAnalysis} title="Rimuovi filtro">
            &times;
          </button>
        </div>
      )}

      <div className="cauzioni-search">
        <SearchIcon />
        <input
          type="search"
          placeholder="Cerca per garanzia o rischio..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Cerca una garanzia"
          data-testid="cauzioni-search"
        />
      </div>

      {loading && <p className="cauzioni-count">Caricamento delle garanzie...</p>}

      {!loading && error && (
        <p className="cauzioni-count cauzioni-error">
          Impossibile caricare le garanzie. Riprova più tardi.
        </p>
      )}

      {!loading && !error && !filtered.length && (
        <p className="cauzioni-count">Nessuna garanzia corrisponde alla ricerca.</p>
      )}

      {!loading && !error && filtered.length > 0 && (
        <p className="cauzioni-count">
          Seleziona Prodotto
        </p>
      )}

      <div className="cauzioni-grid">
        {filtered.map((sub) => (
          /* The whole tile is the action — no buttons. Info moved to the rischio screen, where
             the document list is specific to the rischio rather than an aggregate. */
          <button
            type="button"
            className="cauzioni-card cauzioni-card-clickable"
            key={sub.id}
            data-testid="cauzioni-card"
            data-slug={sub.slug}
            onClick={() => onRequestPolicy(sub)}
          >
            <div className="cauzioni-card-body">
              <h2 className="cauzioni-card-title">{sub.name}</h2>
              {sub.description && (
                <p className="cauzioni-card-description">{sub.description}</p>
              )}
              <span className="cauzioni-badge">
                {sub.risks.length === 1 ? '1 rischio' : `${sub.risks.length} rischi`}
              </span>
              {search.trim() && !sub.name.toLowerCase().includes(search.trim().toLowerCase()) && (
                <p className="cauzioni-risk-match">
                  {sub.risks
                    .filter((r) => r.name.toLowerCase().includes(search.trim().toLowerCase()))
                    .map((r) => r.name)
                    .join(', ')}
                </p>
              )}
              {/* The reason the AI chose this one. Shown per card rather than once at the top:
                  with more than one match the broker is being asked to choose between them, and
                  that choice needs the justification next to each option. */}
              {reasonBySubProduct.has(sub.id) && (
                <p className="cauzioni-ai-reason">
                  <strong>{`Corrispondenza ${reasonBySubProduct.get(sub.id).confidence}: `}</strong>
                  {reasonBySubProduct.get(sub.id).reason}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>

      {infoSubProduct && (
        <CauzioniInfoModal
          subProduct={infoSubProduct}
          onClose={() => setInfoSubProduct(null)}
        />
      )}
    </div>
  );
}

export default CauzioniSubProducts;
